import { useFormik, FormikProvider, FieldArray } from 'formik';
import { Grid, Flex } from 'common/containers';
import * as Yup from 'yup';
import 'utils/yupExtensions';
import useManageForms from 'hooks/useManageForms';
import { Subtitle } from 'common/text';
import uuidv4 from 'utils/uuid';
import ButtonCircle from 'common/buttons/circle';
import Delete from 'common/buttons/delete';
import api from 'services/api';
// inputs
import Input from 'components/forms/inputs/input';
import Select from 'components/forms/inputs/select';
import SelectFetch from 'components/forms/inputs/fetchSelect';
import Submit from 'components/forms/inputs/submit';
import { useState } from 'react';

const AddProductForm = () => {
	const { submit, check, checkZipStatus } = useManageForms();
	const [zipWarning, setZipWarning] = useState<string | null>(null);
	const formik = useFormik({
		initialValues: {
			orderStatusId: '',
			businessUnit: '',
			customer: '',
			customerPhone: '',
			customerAddress: '',
			customerZipCode: '',
			originOfSell: '',
			products: [
				{
					productId: '',
					quantity: '',
					price: '',
					key: uuidv4(),
				},
			],
			paymentMethodId: '',
			carrierGuide: '',
			edd: '',
			comments: '',
		},
		validationSchema: Yup.object({
			orderStatusId: Yup.string().required('Este campo es obligatorio'),
			businessUnit: Yup.string().required('Este campo es obligatorio'),
			customer: Yup.string().required('Este campo es obligatorio'),
			customerPhone: Yup.number().required('Este campo es obligatorio'),
			customerAddress: Yup.string().required('Este campo es obligatorio'),
			customerZipCode: Yup.string().required('Este campo es obligatorio'),
			originOfSell: Yup.string().required('Este campo es obligatorio'),
			products: Yup.array().of(
				Yup.object().shape({
					productId: Yup.string().required('Este campo es obligatorio'),
					quantity: Yup.number().required('Este campo es obligatorio'),
					price: Yup.number().required('Este campo es obligatorio'),
				})
			),
		}),
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true);
			let checkOrder = await check({ customerPhone: values.customerPhone }, '/orders/check');
			let validateZipStatusCall = await checkZipStatus(values.customerZipCode);
			if (checkOrder && validateZipStatusCall) {
				await submit(values, '/orders', 'Order creada', '/orders');
			} else {
				setSubmitting(false);
			}
		},
	});
	const isLastRateEmpty = (products: any) => {
		const lastProduct = products[products.length - 1];
		return lastProduct.price === '' || lastProduct.quantity === '' || lastProduct.productId === '';
	};
	const validateZipStatus = async (zipCode: string) => {
		if (!zipCode) return;
		try {
			const response = await api.post('/orders/check-zip-status', { zip: zipCode });

			const { status, message } = response.data;
			if (status === false) {
				setZipWarning(message);
			} else {
				setZipWarning(null);
			}
		} catch (error) {
			setZipWarning(null);
		}
	};
	return (
		<FormikProvider value={formik}>
			<form onSubmit={formik.handleSubmit}>
				{/* basic fields */}
				<Grid
					$columns={1}
					$margin="2rem">
					<Subtitle $separator={true}>Orden</Subtitle>
					<SelectFetch
						url="/order-status"
						placeholder="Estado de la orden"
						name="orderStatusId"
						searchedValue="_id"
						searchedLabel="name"
					/>
				</Grid>
				<Grid
					$columns={2}
					$margin="2rem">
					<Select
						name="businessUnit"
						placeholder="Unidad de negocio"
						options={[
							{ value: 'Keralazzio', label: 'Keralazzio' },
							{ value: 'Nanoplastec', label: 'Nanoplastec' },
						]}
					/>
					<Select
						name="originOfSell"
						placeholder="Origen de la venta"
						options={[
							{ value: 'mercado-libre', label: 'Mercado Libre' },
							{ value: 'facebook', label: 'Facebook' },
							{ value: 'whatsapp', label: 'Whatsapp' },
							{ value: 'e-commerce', label: 'E-commerce' },
						]}
					/>
				</Grid>
				<Grid
					$columns={1}
					$margin="2rem">
					<Subtitle $separator={true}>Cliente</Subtitle>
					<Input
						nameData="customer"
						placeholderData="Cliente"
						typeData="text"
						label="Cliente"
					/>
					<Input
						nameData="customerPhone"
						placeholderData="Teléfono cliente"
						typeData="text"
						label="Teléfono cliente"
					/>
					<Input
						nameData="customerAddress"
						placeholderData="Dirección"
						typeData="text"
						label="Dirección"
					/>
					<Input
						nameData="customerZipCode"
						placeholderData="Código Postal"
						typeData="text"
						label="Código Postal"
						onBlur={(e: any) => {
							formik.handleBlur(e); // Mantener la funcionalidad de Formik
							validateZipStatus(e.target.value); // Disparar nuestra validación informativa
						}}
					/>
				</Grid>

				<FieldArray
					name="products"
					render={arrayHelpers => {
						return (
							<>
								<Subtitle
									$separator={true}
									$center="space-between">
									Productos
									<ButtonCircle
										disabled={isLastRateEmpty(formik.values.products)}
										$icon="add"
										onClick={() => {
											arrayHelpers.push({
												productId: '',
												quantity: '',
												price: '',
												key: uuidv4(),
											});
										}}
									/>
								</Subtitle>
								{formik.values.products.map((product, index) => (
									<Grid
										$columns={1}
										$separator="2rem"
										$line={true}
										key={product.key}>
										<SelectFetch
											url="/products"
											placeholder="Producto"
											name={`products[${index}].productId`}
											searchedValue="_id"
											searchedLabel="name"
										/>
										<Input
											nameData={`products[${index}].quantity`}
											placeholderData="Piezas"
											typeData="number"
											label="Piezas"
										/>
										<Input
											nameData={`products[${index}].price`}
											placeholderData="Precio"
											typeData="number"
											label="Precio"
										/>
										<Flex $h="flex-end">
											<Delete
												disabled={formik.values.products.length === 1}
												onClick={() => arrayHelpers.remove(index)}
											/>
										</Flex>
									</Grid>
								))}
							</>
						);
					}}
				/>
				<Grid
					$columns={1}
					$separator="2rem">
					<Subtitle $separator={true}>Pago y envio</Subtitle>
					<SelectFetch
						url="/payment-methods"
						placeholder="Método de pago"
						name="paymentMethodId"
						searchedValue="_id"
						searchedLabel="bank"
					/>
					<Input
						nameData="carrierGuide"
						placeholderData="Guía de envío"
						typeData="text"
						label="Guía de envío"
					/>
					<Input
						nameData="edd"
						placeholderData="Tiempo de envío"
						typeData="text"
						label="Tiempo de envío"
					/>
					<Input
						nameData="comments"
						placeholderData="Comentarios"
						typeData="text"
						label="Comentarios"
					/>
				</Grid>

				{zipWarning && (
					<div style={{ color: 'red', fontSize: '1rem', marginTop: '2rem', textAlign: 'right' }}>
						{zipWarning}
					</div>
				)}
				<Flex $margin="2rem 0 0 0">
					<Submit
						onClick={formik.handleSubmit}
						disabled={formik.isSubmitting}>
						Salvar
					</Submit>
				</Flex>
			</form>
		</FormikProvider>
	);
};
export default AddProductForm;
